import './StreamingPacing.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { CSSProperties, ReactNode, useCallback, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { MdOutlineFileDownload, MdOutlineMenuBook, MdWarning } from 'react-icons/md';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatPacingNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { Dropdown } from '@Components/Dropdown/Dropdown';
import { FilterBar } from '@Components/FilterBar';
import { Button } from '@Components/Button';
import { GlossaryModal } from '@Components/GlossaryModal/GlossaryModal';
import { IconButton } from '@Components/IconButton/IconButton';
import { Page } from '@Components/Page/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { ChartTooltipV2, ColorKey, KeyShape } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { BreadcrumbTitle } from '@Components/BreadcrumbTitle';
import { LAYER_HEIGHT, MARKER_OVERHANG, PacingBar } from '@Pages/Pacing/components/PacingBar';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { EmptyState } from '@Components/EmptyState';
import { AxesSkeleton, BarGraphSkeleton, ListSkeleton, Skeleton } from '@Components/Skeleton';
import { Background, Design, Placement, Size } from '@Components/designConstants';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { useSetError } from '@src/redux/modals';
import { download } from '@src/utils/download-utils';
import { PacingBarColors } from '@src/utils/colors';
import { StreamingPacingChart } from './StreamingPacingChart';
import { STREAMING_PACING_GLOSSARY } from './streaming-pacing-glossary';
import { makeCompanyLabel, makeNetworkLabel, StreamingPacingPane } from './StreamingPacingPane';
import { FETCH_FAILED_TITLE, useStreamingPacing } from './use-streaming-pacing';
import {
  ADJUSTED_LABEL,
  BY_COMPANY_KEY,
  BY_COMPANY_LABEL,
  BY_NETWORK_KEY,
  DimensionKey,
  IMPRESSIONS_LABEL,
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_BOTH,
  MEDIA_TYPE_STREAMING,
  PLATFORM_DISPLAY,
  PLATFORM_STREAMING,
  PacingSubData,
  SIMPLE_LABEL,
  SPEND_LABEL,
  SimplicityKey,
  USE_NETWORK_GROUP_KEY,
  USE_NETWORK_GROUP_LABEL,
  USE_NETWORK_KEY,
  USE_NETWORK_LABEL
} from './streaming-pacing-types';
import {
  buildChartRows,
  filterLines,
  formatPacingPercent,
  hasPacingAlert,
  itemTotals,
  LineItem,
  mergeItems,
  sortByPacingGap
} from './streaming-pacing-utils';

const GROUPING_LABELS = [BY_COMPANY_LABEL, USE_NETWORK_LABEL, USE_NETWORK_GROUP_LABEL];
const MEDIA_TYPE_OPTIONS = [
  { label: 'Both', value: MEDIA_TYPE_BOTH },
  { label: 'Streaming', value: MEDIA_TYPE_STREAMING },
  { label: 'Audio', value: MEDIA_TYPE_AUDIO }
];
interface LegendEntry {
  color: string;
  shape: KeyShape;
  label: string;
  tooltip: string;
}

// Delivery and pacing are stacked layers (squares); booked is the marker (a line).
const LEGEND: LegendEntry[] = [
  {
    color: PacingBarColors.deliveryUnsecured,
    shape: 'square',
    label: 'Delivery',
    tooltip: 'What has delivered so far in the selected date range.'
  },
  {
    color: PacingBarColors.pacing,
    shape: 'square',
    label: 'Pacing',
    tooltip: 'What is projected to deliver on top of delivery by the end of the date range.'
  },
  {
    color: PacingBarColors.booked,
    shape: 'line',
    label: 'Booked',
    tooltip: 'What is booked for the date range, per the Simple or Adjusted setting.'
  }
];
const LEFT_FILTER_OPTIONS_BY_COMPANY = ['company'];
const LEFT_FILTER_OPTIONS_BY_NETWORK = ['network', 'description', 'platform', 'audio_buy_type'];
const RIGHT_FILTER_OPTIONS_BY_COMPANY = LEFT_FILTER_OPTIONS_BY_NETWORK;
const RIGHT_FILTER_OPTIONS_BY_NETWORK = LEFT_FILTER_OPTIONS_BY_COMPANY;

// Lets the SCSS size rows from the shared PacingBar's dimensions.
const BAR_SIZE_VARS = {
  '--pacing-bar-height': `${LAYER_HEIGHT}px`,
  '--pacing-bar-margin': `${MARKER_OVERHANG}px`
} as CSSProperties;

const FETCH_FAILED_SUBTITLE = 'Try changing the date range or refreshing the page.';

const ROW_HEIGHT = LAYER_HEIGHT + 2 * MARKER_OVERHANG;
const SKELETON_PADDING = 16;

const RowsSkeleton = () => (
  <div className='streaming-pacing-skeleton'>
    <Skeleton>
      <ListSkeleton
        lineHeight={ROW_HEIGHT}
        lineSpacing={SKELETON_PADDING}
        horizontalPadding={SKELETON_PADDING}
        verticalPadding={SKELETON_PADDING}
        alignTop
        borderRadius={8}
      />
    </Skeleton>
  </div>
);

const ChartSkeleton = () => (
  <div className='streaming-pacing-skeleton'>
    <Skeleton>
      <AxesSkeleton />
      <BarGraphSkeleton />
    </Skeleton>
  </div>
);

const EMPTY_TOTALS = { impressions: 0, spend: 0, netSpend: 0 };
const EMPTY_SUMMARY = {
  booked: { simple: EMPTY_TOTALS, adjusted: EMPTY_TOTALS },
  cleared: { simple: EMPTY_TOTALS, adjusted: EMPTY_TOTALS },
  projection: { simple: EMPTY_TOTALS, adjusted: EMPTY_TOTALS }
};

interface StreamingPacingProps {
  platform: typeof PLATFORM_STREAMING | typeof PLATFORM_DISPLAY;
}

export const StreamingPacing = ({ platform: pagePlatform }: StreamingPacingProps) => {
  const setError = useSetError();
  const isDisplay = pagePlatform === PLATFORM_DISPLAY;
  const pageName = isDisplay ? PAGE_NAMES.DISPLAY_PACING : PAGE_NAMES.STREAMING_PACING;
  const [platform, setPlatform] = useState<string>(isDisplay ? pagePlatform : MEDIA_TYPE_BOTH);
  const {
    data,
    dates,
    fetchFailed,
    key: selectedLeft,
    subKey: selectedRight,
    grouping,
    setDates,
    setGrouping,
    setKey,
    setSubKey,
    useNetworkGroup,
    setUseNetworkGroup
  } = useStreamingPacing(platform, setError);
  const groupByCompany = grouping === BY_COMPANY_KEY;
  const { start, end } = dates;
  const [dimension, setDimension] = useState(isDisplay ? SPEND_LABEL : IMPRESSIONS_LABEL);
  const [simplicity, setSimplicity] = useState(ADJUSTED_LABEL);
  const [showGlossary, setShowGlossary] = useState(false);
  const [leftFilter, setLeftFilter] = useState<(line: object) => boolean>(() => () => true);
  const [rightFilter, setRightFilter] = useState<(line: object) => boolean>(() => () => true);

  const dimensionKey: DimensionKey = dimension === SPEND_LABEL ? 'spend' : 'impressions';
  const numberPrefix = dimension === SPEND_LABEL ? '$' : '';
  const simplicityKey: SimplicityKey = simplicity === SIMPLE_LABEL ? 'simple' : 'adjusted';

  const rangeCount = useMemo(
    () => Dfns.differenceInCalendarDays(Dfns.parseISO(start), Dfns.parseISO(end)) + 1,
    [start, end]
  );

  const formatNumber = useCallback(
    (value: number) => formatPacingNumber(value, numberPrefix),
    [numberPrefix]
  );

  const overlayRenderer = useCallback(
    (item: LineItem) => {
      const { booked, cleared, projection } = itemTotals(item, simplicityKey, dimensionKey);
      return (
        <ChartTooltipV2
          headers={['key' in item ? item.key : 'Total']}
          itemKeyShape='square'
          items={[
            {
              label: 'Delivered',
              value: formatNumber(cleared),
              color: PacingBarColors.deliveryUnsecured
            },
            { label: 'Pacing', value: formatNumber(projection), color: PacingBarColors.pacing },
            { label: 'Booked', value: formatNumber(booked), color: PacingBarColors.booked },
            { label: 'Δ', value: formatNumber(booked - projection), color: undefined }
          ]}
        />
      );
    },
    [dimensionKey, formatNumber, simplicityKey]
  );

  const { leftMax, leftData } = useMemo(() => {
    if (!data) {
      return { leftMax: 0, leftData: [] };
    }
    const filtered = selectedLeft
      ? data.filter(elem => elem.key === selectedLeft)
      : data.filter(leftFilter);
    const { sorted, max } = sortByPacingGap(filtered, simplicityKey, dimensionKey);
    return { leftMax: max, leftData: sorted };
  }, [data, dimensionKey, leftFilter, selectedLeft, simplicityKey]);

  const rightData = useMemo(
    () => (data && selectedLeft ? data.find(elem => elem.key === selectedLeft) : undefined),
    [data, selectedLeft]
  );

  const { rightMax, rightDataFiltered, rightDataSummary } = useMemo(() => {
    if (!rightData?.subData) {
      return { rightMax: 0, rightDataFiltered: [] as PacingSubData[], rightDataSummary: undefined };
    }
    const filtered = selectedRight
      ? rightData.subData.filter(elem => elem.key === selectedRight)
      : rightData.subData.filter(rightFilter);
    const { sorted, max } = sortByPacingGap(filtered, simplicityKey, dimensionKey);
    return {
      rightMax: max,
      rightDataFiltered: sorted,
      rightDataSummary: { ...EMPTY_SUMMARY, ...mergeItems(sorted), start, end }
    };
  }, [dimensionKey, end, rightData, rightFilter, selectedRight, simplicityKey, start]);

  const summaryKpis = useMemo(() => {
    if (!rightDataSummary) {
      return undefined;
    }
    const { booked, cleared, projection } = itemTotals(
      rightDataSummary,
      simplicityKey,
      dimensionKey
    );
    return [
      { label: 'Booked', value: formatNumber(booked) },
      { label: 'Delivered', value: formatNumber(cleared) },
      { label: 'Pacing', value: formatNumber(projection) },
      {
        label: 'Pacing vs Booked',
        value: formatPacingPercent(booked, projection)
      }
    ];
  }, [dimensionKey, formatNumber, rightDataSummary, simplicityKey]);

  const hasRightAlert = useMemo(
    () => rightDataFiltered.some(elem => hasPacingAlert(elem, simplicityKey, dimensionKey)),
    [dimensionKey, rightDataFiltered, simplicityKey]
  );
  const hasLeftAlert = useMemo(
    () => leftData.some(elem => hasPacingAlert(elem, simplicityKey, dimensionKey)),
    [dimensionKey, leftData, simplicityKey]
  );

  const { chartData, chartDataBooked } = useMemo(() => {
    const group = rightData?.subData?.find(elem => elem.key === selectedRight);
    if (!group?.days) {
      return {};
    }
    const rows = buildChartRows(group.days, start, rangeCount, simplicityKey, dimensionKey);
    return { chartData: rows, chartDataBooked: group.booked[simplicityKey][dimensionKey] };
  }, [dimensionKey, rangeCount, rightData, selectedRight, simplicityKey, start]);

  const renderBar = (item: LineItem, max: number, reserveAlert: boolean): ReactNode => {
    const { booked, cleared, projection } = itemTotals(item, simplicityKey, dimensionKey);
    // The summary line scales to its own totals instead of the pane's max.
    const barMax = 'independent' in item ? Math.max(booked, cleared, projection) : max;
    const hasAlert = 'alert' in item && item.alert?.[simplicityKey]?.[dimensionKey];
    // `false` keeps the alert slot empty so every row in the pane lines up.
    let alert: ReactNode;
    if (hasAlert) {
      alert = (
        <OverlayTrigger
          placement={Placement.TOP}
          overlay={<Tooltip>Pacing needs attention</Tooltip>}
        >
          <span className='streaming-pacing-alert'>
            <MdWarning />
          </span>
        </OverlayTrigger>
      );
    } else if (reserveAlert) {
      alert = false;
    }
    return (
      <PacingBar
        max={barMax}
        // Delivered, then the projected remainder on top of it.
        layers={[
          {
            key: 'delivery',
            value: cleared,
            color: PacingBarColors.deliveryUnsecured,
            label: formatNumber(cleared)
          },
          {
            key: 'pacing',
            value: Math.max(projection - cleared, 0),
            color: PacingBarColors.pacing,
            label: formatNumber(projection)
          }
        ]}
        markers={[{ key: 'booked', value: booked, color: PacingBarColors.booked }]}
        endLabel={formatPacingPercent(booked, projection)}
        alert={alert}
        overlay={overlayRenderer(item)}
        ariaLabel={`${'key' in item ? item.key : 'Total'}: delivered ${formatNumber(cleared)}, pacing ${formatNumber(projection)}, booked ${formatNumber(booked)}${hasAlert ? ', needs attention' : ''}`}
      />
    );
  };

  const exportCsv = () => {
    const csvRows = [
      [
        'Network',
        'Network Description',
        'Derived Network ID',
        'Platform',
        'Start Date',
        'End Date',
        `Booked ${dimension}`,
        `Pacing ${dimension}`,
        'Clearance Rate'
      ],
      ...rightDataFiltered.map(row => [
        row.network,
        row.description,
        row.derived_network,
        row.platform,
        start,
        end,
        row.booked[simplicityKey][dimensionKey],
        row.projection[simplicityKey][dimensionKey],
        row.booked[simplicityKey][dimensionKey]
          ? row.projection[simplicityKey][dimensionKey] / row.booked[simplicityKey][dimensionKey]
          : ''
      ])
    ];
    download(
      Papa.unparse(csvRows),
      `${selectedLeft}_clearance_rates_${simplicityKey}_${dimensionKey}_${start}-${end}.csv`,
      'text/csv'
    );
  };

  const getGroupingValue = (): string => {
    if (groupByCompany) {
      return BY_COMPANY_LABEL;
    }
    return useNetworkGroup === USE_NETWORK_GROUP_KEY ? USE_NETWORK_GROUP_LABEL : USE_NETWORK_LABEL;
  };
  const groupingValue = getGroupingValue();

  const onGroupingChange = (newGrouping: string) => {
    if (newGrouping === BY_COMPANY_LABEL) {
      setGrouping(BY_COMPANY_KEY);
    } else {
      setGrouping(BY_NETWORK_KEY);
      setUseNetworkGroup(
        newGrouping === USE_NETWORK_GROUP_LABEL ? USE_NETWORK_GROUP_KEY : USE_NETWORK_KEY
      );
    }
    setKey(undefined);
    setSubKey(undefined);
  };

  const breadcrumbs = useMemo(
    () => [
      ...(selectedLeft
        ? [{ key: 'left', label: selectedLeft, onClick: () => setSubKey(undefined) }]
        : []),
      ...(selectedRight ? [{ key: 'right', label: selectedRight }] : [])
    ],
    [selectedLeft, selectedRight, setSubKey]
  );

  const showMediaType =
    !isDisplay &&
    !((groupByCompany && selectedRight) || (grouping === BY_NETWORK_KEY && selectedLeft));

  const leftFilterOptions = groupByCompany
    ? LEFT_FILTER_OPTIONS_BY_COMPANY
    : LEFT_FILTER_OPTIONS_BY_NETWORK;
  const rightFilterOptions = groupByCompany
    ? RIGHT_FILTER_OPTIONS_BY_COMPANY
    : RIGHT_FILTER_OPTIONS_BY_NETWORK;
  // The filter bar rebuilds its options whenever `lines` changes identity, so keep it stable.
  const leftFilterLines = useMemo(
    () => filterLines(data ?? [], leftFilterOptions),
    [data, leftFilterOptions]
  );
  const rightFilterLines = useMemo(
    () => filterLines(rightData?.subData ?? [], rightFilterOptions),
    [rightData, rightFilterOptions]
  );

  return (
    <Page
      title={
        <BreadcrumbTitle
          title={pageName}
          path={breadcrumbs}
          onTitleClick={() => {
            setKey(undefined);
            setSubKey(undefined);
          }}
        />
      }
      pageType={pageName}
      isLoading={!data && !fetchFailed}
      actions={
        <div className='streaming-pacing-actions'>
          <SegmentedControlButton
            background={Background.DARK}
            options={GROUPING_LABELS}
            selectedOption={groupingValue}
            onChange={onGroupingChange}
          />
          {showMediaType && (
            <Dropdown
              label='Media Type'
              value={platform}
              options={MEDIA_TYPE_OPTIONS}
              onChange={setPlatform}
            />
          )}
          <DatePicker
            range={dates}
            onChange={setDates}
            disabled={!data}
            removeHeaderText
            isOutsideRange={() => false}
            datePresets
          />
        </div>
      }
      subHeader={
        <div className='streaming-pacing-sub-header'>
          {!isDisplay && (
            <SegmentedControlButton
              size={Size.MD}
              design={Design.NEUTRAL}
              options={[SIMPLE_LABEL, ADJUSTED_LABEL]}
              selectedOption={simplicity}
              onChange={setSimplicity}
            />
          )}
          <SegmentedControlButton
            size={Size.MD}
            design={Design.NEUTRAL}
            options={[IMPRESSIONS_LABEL, SPEND_LABEL]}
            selectedOption={dimension}
            onChange={setDimension}
          />
          <div className='streaming-pacing-legend' data-testid='pacing-legend'>
            {LEGEND.map(({ color, shape, label, tooltip }) => (
              <OverlayTrigger
                key={label}
                placement={Placement.BOTTOM}
                overlay={<Tooltip>{tooltip}</Tooltip>}
              >
                <div className='streaming-pacing-legend-group'>
                  <ColorKey shape={shape} color={color} />
                  {label}
                </div>
              </OverlayTrigger>
            ))}
          </div>
          <Button
            size={Size.MD}
            design={Design.NEUTRAL}
            leadingElement={<MdOutlineMenuBook />}
            onClick={() => {
              Mixpanel.track(MxE.ModalOpened, { page: pageName, modal: 'pacing glossary' });
              setShowGlossary(true);
            }}
          >
            Glossary
          </Button>
        </div>
      }
    >
      <div className='streaming-pacing' data-testid='streaming-pacing' style={BAR_SIZE_VARS}>
        {data && (
          <div className='pacing-panes'>
            <div className={cn('pacing-left-pane', { condensed: selectedLeft })}>
              <StreamingPacingPane
                simplicityKey={simplicityKey}
                title={
                  selectedLeft
                    ? undefined
                    : `${groupByCompany ? 'Companies' : 'Networks'} (${leftData.length})`
                }
                items={leftData}
                selectedKey={selectedLeft}
                onSelect={key => {
                  setSubKey(undefined);
                  setKey(key);
                }}
                filter={
                  selectedLeft ? undefined : (
                    <FilterBar
                      size={Size.MD}
                      storageKeySuffix='left'
                      hasAdvanced
                      options={leftFilterOptions}
                      lines={leftFilterLines}
                      onFilter={filter => setLeftFilter(() => filter)}
                    />
                  )
                }
                labelRenderer={groupByCompany ? makeCompanyLabel : makeNetworkLabel}
                lineItemRenderer={
                  selectedLeft ? undefined : item => renderBar(item, leftMax, hasLeftAlert)
                }
              />
            </div>
            {selectedLeft &&
              (rightData?.subData ? (
                <div className='pacing-right-pane'>
                  <div className={cn('pacing-top-pane', { condensed: selectedRight })}>
                    <StreamingPacingPane
                      simplicityKey={simplicityKey}
                      title={
                        selectedRight
                          ? undefined
                          : `${groupByCompany ? 'Networks' : 'Companies'} (${rightDataFiltered.length})`
                      }
                      items={rightDataFiltered}
                      selectedKey={selectedRight}
                      onSelect={setSubKey}
                      summaryData={selectedRight ? undefined : rightDataSummary}
                      kpis={selectedRight ? undefined : summaryKpis}
                      filter={
                        selectedRight ? undefined : (
                          <>
                            <FilterBar
                              size={Size.MD}
                              storageKeySuffix='right'
                              hasAdvanced
                              options={rightFilterOptions}
                              lines={rightFilterLines}
                              onFilter={filter => setRightFilter(() => filter)}
                            />
                            <IconButton
                              size={Size.MD}
                              tooltip={{ text: 'Download CSV' }}
                              onClick={exportCsv}
                            >
                              <MdOutlineFileDownload />
                            </IconButton>
                          </>
                        )
                      }
                      labelRenderer={groupByCompany ? makeNetworkLabel : makeCompanyLabel}
                      lineItemRenderer={item => renderBar(item, rightMax, hasRightAlert)}
                    />
                  </div>
                  {selectedRight &&
                    (chartData ? (
                      <div className='pacing-bottom-pane' data-testid='streaming-pacing-chart'>
                        <StreamingPacingChart
                          data={chartData}
                          booked={chartDataBooked}
                          title={`Daily ${dimension}`}
                          subtitle={`${start} to ${end}`}
                          formatNumber={formatNumber}
                        />
                      </div>
                    ) : (
                      <div className='pacing-bottom-pane'>
                        {fetchFailed ? (
                          <EmptyState title={FETCH_FAILED_TITLE} subtitle={FETCH_FAILED_SUBTITLE} />
                        ) : (
                          <ChartSkeleton />
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className='pacing-right-pane'>
                  <div className='pacing-top-pane'>
                    {fetchFailed ? (
                      <EmptyState title={FETCH_FAILED_TITLE} subtitle={FETCH_FAILED_SUBTITLE} />
                    ) : (
                      <RowsSkeleton />
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
        {!data && fetchFailed && (
          <EmptyState title={FETCH_FAILED_TITLE} subtitle={FETCH_FAILED_SUBTITLE} />
        )}
        {!data && !fetchFailed && (
          <div className='pacing-panes'>
            <div className='pacing-left-pane'>
              <RowsSkeleton />
            </div>
          </div>
        )}
      </div>
      <GlossaryModal
        show={showGlossary}
        setShow={setShowGlossary}
        title='Pacing Glossary'
        initialGlossary={STREAMING_PACING_GLOSSARY}
      />
    </Page>
  );
};
